import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Menu, X, ChevronDown, LogOut, User, LayoutDashboard, Settings } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";

export default function Navbar({ transparent = false }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const dashboardPath = user?.role === "admin" ? "/admin" : user?.role === "council_member" ? "/council" : "/dashboard";

  return (
    <nav
      data-testid="main-navbar"
      className="fixed w-full z-50 bg-[#FAFAF8]/95 backdrop-blur-xl border-b border-[#E4E4E1]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group" data-testid="logo-link">
            <div className="w-7 h-7 bg-[#0F172A] rounded-sm flex items-center justify-center">
              <span className="text-white text-xs font-bold font-serif">C</span>
            </div>
            <span className="text-[#0F172A] font-semibold text-base tracking-tight">
              Council<span className="text-[#064E3B]">One</span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            <Link to="/discover" className="text-sm text-[#52525B] hover:text-[#0F172A] transition-colors font-medium" data-testid="nav-discover">
              Find Experts
            </Link>
            <Link to="/knowledge" className="text-sm text-[#52525B] hover:text-[#0F172A] transition-colors font-medium" data-testid="nav-knowledge">
              Knowledge Hub
            </Link>
            <a href="#offerings" className="text-sm text-[#52525B] hover:text-[#0F172A] transition-colors font-medium">
              How it Works
            </a>
          </div>

          {/* Auth Actions */}
          <div className="hidden md:flex items-center gap-3">
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="flex items-center gap-2 px-3 py-1.5 rounded-sm hover:bg-[#F4F4F1] transition-colors"
                    data-testid="user-menu-trigger"
                  >
                    {user.picture ? (
                      <img src={user.picture} alt={user.name} className="w-7 h-7 rounded-full object-cover" />
                    ) : (
                      <div className="w-7 h-7 rounded-full bg-[#0F172A] flex items-center justify-center text-white text-xs font-semibold">
                        {user.name?.charAt(0) || "U"}
                      </div>
                    )}
                    <span className="text-sm font-medium text-[#0F172A]">{user.name?.split(" ")[0]}</span>
                    <ChevronDown size={14} className="text-[#52525B]" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 bg-white border border-[#E4E4E1] shadow-sm rounded-sm">
                  <DropdownMenuItem asChild>
                    <Link to={dashboardPath} className="flex items-center gap-2 text-sm" data-testid="nav-dashboard">
                      <LayoutDashboard size={14} /> Dashboard
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="flex items-center gap-2 text-sm text-red-600" data-testid="nav-logout">
                    <LogOut size={14} /> Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Link to="/login" className="text-sm font-medium text-[#52525B] hover:text-[#0F172A] transition-colors px-3 py-2" data-testid="nav-login">
                  Sign in
                </Link>
                <Link to="/login" className="btn-primary text-sm px-5 py-2" data-testid="nav-register">
                  Join as Explorer
                </Link>
              </>
            )}
          </div>

          {/* Mobile toggle */}
          <button
            className="md:hidden p-2 text-[#0F172A]"
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden bg-[#FAFAF8] border-t border-[#E4E4E1] px-4 py-4 space-y-3" data-testid="mobile-menu">
          <Link to="/discover" className="block text-sm font-medium text-[#0F172A] py-2" onClick={() => setMobileOpen(false)}>Find Experts</Link>
          <Link to="/knowledge" className="block text-sm font-medium text-[#0F172A] py-2" onClick={() => setMobileOpen(false)}>Knowledge Hub</Link>
          {user ? (
            <>
              <Link to={dashboardPath} className="block text-sm font-medium text-[#0F172A] py-2" onClick={() => setMobileOpen(false)}>Dashboard</Link>
              <button onClick={handleLogout} className="block text-sm font-medium text-red-600 py-2 w-full text-left">Sign out</button>
            </>
          ) : (
            <>
              <Link to="/login" className="block" onClick={() => setMobileOpen(false)}>
                <button className="btn-primary w-full text-center py-2.5">Get Started</button>
              </Link>
            </>
          )}
        </div>
      )}
    </nav>
  );
}
